// Right-hand drawer: everything about the selected thing, with its photos, linked trips and actions.
import { useState, useEffect } from 'preact/hooks';
import { html, md, Gallery, ConfirmButton, openModal, notify, fmtDate, fmtDT } from './common.js';
import { get, post, del } from '../api.js';
import { selection, me, viewer, refresh, places, setFilter, model, studyFocus, dataVersion, canWrite, feat, cavePlaces, openSurveyData } from '../store.js';
import { FEATURE_KINDS, OBS_CATEGORIES, STUDY_KINDS, RESULTS, fmtM, placeStyle } from '../viewer/style.js';
import { AttachFiles } from './forms.js';
import { pointView, sinkView, parcelView, PropertyRow } from './tools.js';
import { basinView, springView, HydroRow } from './hydro.js';
import { streamView, traceView, sinkPointView, WaterRow } from './water.js';
import { recordView } from './records.js';
import { caveView, flyToCave } from './caves.js';
import { placeView, showPlace } from './places.js';

const PATH = { feature: 'features', observation: 'observations', study: 'studies', trip: 'trips', cave: 'caves', place: 'cave-places', record: 'records' };

export function Drawer() {
  const sel = selection.value;
  const [data, setData] = useState(null), [err, setErr] = useState(null);
  const ver = dataVersion.value;
  useEffect(() => {
    setErr(null);
    if (sel?.type !== 'place') viewer?.highlightPlace(null);
    viewer?.highlightWater(sel && (sel.type === 'stream' || sel.type === 'trace') ? { type: sel.type, id: sel.id } : null);
    if (!sel) { setData(null); return; }
    if (sel.type === 'study_point') { selection.value = { type: 'study', id: sel.place.study_id }; return; }
    if (!PATH[sel.type]) { setData(sel); return; }
    let live = true;
    if (!data || data.id !== sel.id || (data.type || sel.type) !== sel.type) setData(null);     // don't draw the old record with the new view
    get(`/api/${PATH[sel.type]}/${sel.id}`).then(d => {
      if (!live) return;
      d._k = `${sel.type}:${sel.id}`;
      setData(d);
      if (sel.fly !== false && !sel.noFly) flyToRecord(d);
    }).catch(e => live && setErr(e.message));
    viewer?.select(['study', 'cave', 'place'].includes(sel.type) ? null : { type: sel.type, id: sel.id });
    return () => { live = false; };
  }, [sel?.type, sel?.id, sel?.t, ver]);
  if (!sel) return null;
  const reload = () => { selection.value = { ...sel, t: Date.now(), noFly: true }; };
  const close = () => { selection.value = null; viewer?.select(null); if (studyFocus.value) { studyFocus.value = null; viewer.focusStudy(null); } };
  let body = null, title = '', sub = '', actions = null;
  if (err) body = html`<div class="err">${err}</div>`;
  else if (!PATH[sel.type] && sel.type !== 'study_point') {         // things the viewer already knows all about
    if (sel.type === 'station') ({ body, title, sub, actions } = stationView(sel));
    else if (sel.type === 'depression') ({ body, title, sub, actions } = depressionView(sel));
    else if (sel.type === 'point') ({ body, title, sub, actions } = pointView(sel));
    else if (sel.type === 'sink') ({ body, title, sub, actions } = sinkView(sel));
    else if (sel.type === 'parcel') ({ body, title, sub, actions } = parcelView(sel));
    else if (sel.type === 'basin') ({ body, title, sub, actions } = basinView(sel));
    else if (sel.type === 'spring') ({ body, title, sub, actions } = springView(sel));
    else if (sel.type === 'stream') ({ body, title, sub, actions } = streamView(sel));
    else if (sel.type === 'trace') ({ body, title, sub, actions } = traceView(sel));
    else if (sel.type === 'sinkpoint') ({ body, title, sub, actions } = sinkPointView(sel));
    else if (sel.type === 'xspoint') ({ body, title, sub, actions } = xsPointView(sel));
  }
  else if (!data || data._k !== `${sel.type}:${sel.id}`) body = html`<div class="muted">Loading…</div>`;   // not the old record in the new view
  else if (sel.type === 'feature') ({ body, title, sub, actions } = featureView(data, reload));
  else if (sel.type === 'observation') ({ body, title, sub, actions } = observationView(data, reload));
  else if (sel.type === 'study') ({ body, title, sub, actions } = studyView(data, reload));
  else if (sel.type === 'trip') ({ body, title, sub, actions } = tripView(data, reload));
  else if (sel.type === 'cave') ({ body, title, sub, actions } = caveView(data, reload));
  else if (sel.type === 'place') ({ body, title, sub, actions } = placeView(data, reload));
  else if (sel.type === 'record') ({ body, title, sub, actions } = recordView(data, reload));

  return html`<aside class="drawer" aria-label="Details">
    <header><div style="flex:1;min-width:0"><div class="tiny muted" style="text-transform:uppercase;letter-spacing:.08em">${sub}</div>
      <h2>${title}</h2></div><button class="btn ghost" onClick=${close} aria-label="Close details">✕</button></header>
    <div class="body">${actions && html`<div class="row" style="justify-content:flex-start;flex-wrap:wrap;gap:6px;margin-top:0">${actions}</div>`}${body}</div>
  </aside>`;
}

function flyToRecord(d) {
  if (d.type === 'record') { if (d.in_area) viewer.flyToEN(d.easting, d.northing, viewer.groundAt(d.easting, d.northing), 260); return; }
  if (d.type === 'cave') flyToCave(d);
  else if (d.type === 'place') showPlace(d);
  else if (d.location) viewer.flyToPlace({ loc: d.location });
  else if (d.points?.length) viewer.flyToLocs(d.points.map(p => p.location));
}

function editDelete(type, d, formKind) {
  if (!d.can_edit) return [];
  return [html`<button class="btn sm" onClick=${() => openModal(formKind, { item: d })}>Edit</button>`,
    html`<${ConfirmButton} onConfirm=${async () => { await del(`/api/${PATH[type]}/${d.id}`); selection.value = null; refresh(); notify('Deleted'); }} />`];
}

export function Meta({ d }) {
  return html`<div class="tiny muted" style="margin-top:14px">Added by ${d.created_by_name || '—'} · ${fmtDate(d.created_at)}
    ${d.updated_by_name && d.updated_at !== d.created_at ? ` · edited by ${d.updated_by_name} ${fmtDate(d.updated_at)}` : ''}</div>`;
}

export function LocationLine({ loc }) {
  if (!loc) return null;
  const g = loc.easting != null && viewer ? viewer.groundAt(loc.easting, loc.northing) : null;
  return html`<dt>Location</dt><dd>${loc.station ? html`Station <b>${loc.station}</b>` : loc.on_surface ? 'On the surface' : 'Underground point'}
    ${loc.easting != null && html`<div class="tiny muted num">E ${loc.easting.toFixed(1)} · N ${loc.northing.toFixed(1)}${loc.elevation != null ? ` · ${loc.elevation.toFixed(1)} m` : g ? ` · ground ${g.toFixed(1)} m` : ''}</div>`}
    ${!loc.on_surface && loc.elevation != null && g != null && html`<div class="tiny muted">${(g - loc.elevation).toFixed(0)} m below the LIDAR surface</div>`}</dd>`;
}

export function Media({ d, type, reload, title = 'Photos & documents' }) {
  return html`<div class="sect">${title}</div>
    ${d.media?.length ? html`<${Gallery} media=${d.media} owner=${canWrite() ? { type, id: d.id, reload } : null} />` : html`<div class="muted small">None yet.</div>`}
    ${canWrite() && html`<div style="margin-top:8px"><${AttachFiles} ownerType=${type} ownerId=${d.id} onDone=${reload} /></div>`}`;
}

export function Trips({ d, type, reload }) {
  return html`<div class="sect">Trip reports</div>
    ${d.trips?.length ? html`<div class="list">${d.trips.map(t => html`<button class="row-btn" onClick=${() => selection.value = { type: 'trip', id: t.id }}>
      <span>🗒</span><span class="t">${t.title}</span><span class="s">${t.trip_date}</span></button>`)}</div>` : html`<div class="muted small">None linked.</div>`}
    ${canWrite() && html`<div class="row" style="justify-content:flex-start;gap:6px">
      <button class="btn sm" onClick=${() => openModal('trip', { linkTo: { owner_type: type, owner_id: d.id, label: d.name || d.title } })}>＋ New trip report</button>
      <button class="btn sm" onClick=${() => openModal('linkpicker', { title: 'Link an existing trip report', types: ['trip'],
        onPick: async (r) => { await post(`/api/trips/${r.id}/links`, { owner_type: type, owner_id: d.id }); reload(); notify('Linked'); } })}>Link existing</button></div>`}`;
}

export function Links({ links }) {
  if (!links?.length) return null;
  return html`<div class="sect">Links</div>${links.map(l => html`<div><a href=${l.url} target="_blank" rel="noopener noreferrer">${l.title || l.url}</a></div>`)}`;
}

function featureView(d, reload) {
  const k = FEATURE_KINDS[d.kind] || FEATURE_KINDS.other;
  const actions = [...editDelete('feature', d, 'feature'),
    canWrite() && html`<button class="btn sm" onClick=${() => openModal('observation', { preset: { feature_id: d.id, cave_id: d.cave_id } })}>＋ Observation</button>`];
  const body = html`
    <div class="row" style="justify-content:flex-start;gap:6px">${d.status && html`<span class="pill">${d.status}</span>`}
      ${d.visibility === 'restricted' && html`<span class="pill restricted">restricted</span>`}</div>
    <dl class="kv"><${LocationLine} loc=${d.location} /><${PropertyRow} e=${d.location?.easting} n=${d.location?.northing} /><${HydroRow} e=${d.location?.easting} n=${d.location?.northing} /></dl>
    ${d.description && html`<div class="md" dangerouslySetInnerHTML=${md(d.description)} />`}
    <${Links} links=${d.links} />
    ${d.observations?.length > 0 && html`<div class="sect">Observations</div><div class="list">${d.observations.map(o => html`
      <button class="row-btn" onClick=${() => selection.value = { type: 'observation', id: o.id }}>
        <span class="dot" style=${'background:' + (OBS_CATEGORIES[o.category] || OBS_CATEGORIES.other).color}></span>
        <span class="t">${o.title}${o.count != null ? ` (${o.count})` : ''}</span><span class="s">${o.observed_on || ''}</span></button>`)}</div>`}
    <${Trips} d=${d} type="feature" reload=${reload} />
    <${Media} d=${d} type="feature" reload=${reload} />
    <${Meta} d=${d} />`;
  return { body, title: d.name, sub: k.label, actions };
}

function observationView(d, reload) {
  const c = OBS_CATEGORIES[d.category] || OBS_CATEGORIES.other;
  const body = html`
    ${d.visibility === 'restricted' && html`<span class="pill restricted">restricted</span>`}
    <dl class="kv">
      ${d.observed_on && html`<dt>Observed</dt><dd>${d.observed_on}</dd>`}
      ${d.species && html`<dt>Species / subject</dt><dd>${d.species}</dd>`}
      ${d.count != null && html`<dt>Count</dt><dd>≈ ${d.count}</dd>`}
      ${d.observers && html`<dt>Observers</dt><dd>${d.observers}</dd>`}
      ${d.feature_id && html`<dt>Feature</dt><dd><a href="#" onClick=${(e) => { e.preventDefault(); selection.value = { type: 'feature', id: d.feature_id }; }}>open</a></dd>`}
      <${LocationLine} loc=${d.location} />
    </dl>
    ${d.description && html`<div class="md" dangerouslySetInnerHTML=${md(d.description)} />`}
    <${Trips} d=${d} type="observation" reload=${reload} />
    <${Media} d=${d} type="observation" reload=${reload} />
    <${Meta} d=${d} />`;
  return { body, title: d.title, sub: c.label, actions: editDelete('observation', d, 'observation') };
}

function studyView(d, reload) {
  const k = STUDY_KINDS[d.kind] || STUDY_KINDS.other;
  const focused = studyFocus.value === d.id;
  const src = d.points.filter(p => p.role === 'source'), rec = d.points.filter(p => p.role === 'receptor');
  const pointRow = (p) => {
    const st = placeStyle({ type: 'study_point', kind: p.role, result: p.result, study_kind: d.kind });
    return html`<tr>
      <td><span class="dot" style=${'background:' + st.color}></span></td>
      <td><a href="#" onClick=${(e) => { e.preventDefault(); viewer.flyToPlace({ loc: p.location }); }}>${p.label}</a>
        <div class="tiny muted">${[p.tracer, p.method].filter(Boolean).join(' · ')}${p.location?.station ? ` · ${p.location.station}` : ''}</div></td>
      <td class="tiny">${p.role === 'source' ? fmtDT(p.started_at) : html`${p.result ? (RESULTS[p.result]?.label || p.result) : '—'}<div class="muted">${fmtDT(p.ended_at)}</div>`}
        ${p.value != null ? html`<div class="muted">${p.value} ${p.units || ''}</div>` : ''}</td>
      <td>${d.can_edit && html`<button class="btn sm ghost" onClick=${() => openModal('point', { study: d, item: p })}>Edit</button>`}</td></tr>`;
  };
  const travel = (() => {   // first detection after injection
    const inj = src.map(p => p.started_at).filter(Boolean).sort()[0];
    const det = rec.filter(p => p.result === 'positive' && p.ended_at).map(p => p.ended_at).sort()[0];
    if (!inj || !det) return null;
    const h = (new Date(det) - new Date(inj)) / 3600000;
    return h > 48 ? `${(h / 24).toFixed(1)} days` : `${h.toFixed(1)} hours`;
  })();
  const actions = [
    html`<button class="btn sm" onClick=${() => { const f = focused ? null : d.id; studyFocus.value = f; viewer.focusStudy(f); }}>${focused ? 'Show all studies' : 'Show only this study'}</button>`,
    d.can_edit && html`<button class="btn sm" onClick=${() => openModal('point', { study: d })}>＋ Point</button>`,
    ...editDelete('study', d, 'study')];
  const body = html`
    <dl class="kv">
      ${(d.start_date || d.end_date) && html`<dt>Dates</dt><dd>${d.start_date || '?'} → ${d.end_date || '…'}</dd>`}
      ${d.lead && html`<dt>Lead</dt><dd>${d.lead}</dd>`}
      <dt>Points</dt><dd>${src.length} source${src.length === 1 ? '' : 's'}, ${rec.length} receptor${rec.length === 1 ? '' : 's'}${' '}
        (${rec.filter(p => p.result === 'positive').length} positive)</dd>
      ${travel && html`<dt>First arrival</dt><dd>${travel} after injection</dd>`}
    </dl>
    ${d.points.length > 0 ? html`<table class="t"><tr><th></th><th>Point</th><th>When / result</th><th></th></tr>${[...src, ...rec].map(pointRow)}</table>`
      : html`<div class="muted small">No points yet. Add the injection / fan site and each detector.</div>`}
    ${d.description && html`<div class="sect">Method</div><div class="md" dangerouslySetInnerHTML=${md(d.description)} />`}
    ${d.results && html`<div class="sect">Results</div><div class="md" dangerouslySetInnerHTML=${md(d.results)} />`}
    <${Trips} d=${d} type="study" reload=${reload} />
    <${Media} d=${d} type="study" reload=${reload} />
    <${Meta} d=${d} />`;
  return { body, title: d.title, sub: k.label, actions };
}

function tripView(d, reload) {
  const actions = [...editDelete('trip', d, 'trip'),
    canWrite() && html`<button class="btn sm" onClick=${() => openModal('linkpicker', { title: 'Link this report to…', types: ['feature', 'observation', 'study'],
      onPick: async (r) => { await post(`/api/trips/${d.id}/links`, { owner_type: r.type, owner_id: r.id }); reload(); } })}>＋ Link</button>`];
  const body = html`
    <dl class="kv"><dt>Date</dt><dd>${d.trip_date}${d.end_date ? ` → ${d.end_date}` : ''}</dd>
      ${d.purpose && html`<dt>Purpose</dt><dd>${d.purpose}</dd>`}
      ${d.participants && html`<dt>Participants</dt><dd>${d.participants}</dd>`}
      ${d.external_url && html`<dt>Original</dt><dd><a href=${d.external_url} target="_blank" rel="noopener noreferrer">open link</a></dd>`}</dl>
    ${d.links?.length > 0 && html`<div class="row" style="justify-content:flex-start;flex-wrap:wrap;gap:6px">${d.links.map(l => html`
      <span class="pill" style="cursor:pointer" onClick=${() => { if (['feature', 'observation', 'study'].includes(l.owner_type)) selection.value = { type: l.owner_type, id: l.owner_id }; }}>
        ${l.label}${d.can_edit ? html` <a href="#" aria-label="unlink" onClick=${async (e) => { e.preventDefault(); e.stopPropagation(); await del(`/api/trips/${d.id}/links/${l.id}`); reload(); }}>✕</a>` : ''}</span>`)}</div>`}
    ${d.report ? html`<div class="md" dangerouslySetInnerHTML=${md(d.report)} />` : html`<div class="muted">No report text.</div>`}
    <${Media} d=${d} type="trip" reload=${reload} />
    <${Meta} d=${d} />`;
  return { body, title: d.title, sub: 'Trip report', actions };
}

function xsPointView(d) {
  const x = d.xs, FT = 0.3048;
  const where = x.to_station ? `${(x.along / FT).toFixed(1)} ft from ${x.station} toward ${x.to_station}` : `At station ${x.station}`;
  const actions = [html`<button class="btn sm primary" onClick=${() => openModal('xsection', { station: x.station, to_station: x.to_station, along: x.along || 0 })}>Open the cross-section</button>`,
    html`<button class="btn sm" onClick=${() => viewer.flyToEN(x.e, x.n, x.z, 30)}>Zoom to</button>`];
  const body = html`<dl class="kv"><dt>Where</dt><dd>${where}</dd>
      <dt>Elevation</dt><dd>${fmtM(x.z)}</dd>
      <dt>Drawn by</dt><dd>${x.created_by_name || '—'}${x.created_at ? ` · ${fmtDate(x.created_at)}` : ''}</dd></dl>`;
  return { body, title: x.to_station ? `${x.station} → ${x.to_station}` : x.station, sub: 'Drawn cross-section', actions };
}

function stationView(s) {
  const M = model.value, si = M?.surveys.findIndex(x => x === s.survey);
  const nearby = places.value.filter(p => p.loc?.station === s.name);
  const actions = [
    canWrite() && html`<button class="btn sm" onClick=${() => openModal('observation', { preset: { location: { mode: 'station', station: s.name, elevation: s.elevation } } })}>＋ Observation here</button>`,
    si >= 0 && html`<button class="btn sm" onClick=${() => { setFilter({ surveys: new Set([si]) }); viewer.flyToSurveys(new Set([si])); }}>Isolate this survey</button>`,
    si >= 0 && s.survey.file_id && html`<button class="btn sm" title="The survey's shots as recorded, beside the map" onClick=${() => openSurveyData({ fileId: s.survey.file_id, si, station: s.name })}>Survey data</button>`,
    canWrite() && feat('places') && s.survey.cave_id != null && html`<button class="btn sm" onClick=${() => openModal('place', { preset: { cave_id: s.survey.cave_id, stations: [s.name] } })}>＋ Name a place here</button>`,
    feat('xsections') && html`<button class="btn sm" onClick=${() => openModal('xsection', { station: s.name })} title="The shape of the passage here: view it, draw it or trace it from a map">Cross-section…</button>`];
  const body = html`${s.survey.approx && html`<div class="small" style="border:1px solid rgba(255,210,63,.45);background:rgba(255,210,63,.08);border-radius:8px;padding:6px 8px;margin-bottom:8px">
      <b>Approximate plot.</b> Traced from a club map, not a survey: positions are good to perhaps 10–30 m and depths to a few tens of metres.</div>`}
    <dl class="kv">
      <dt>Survey</dt><dd>${s.survey.name || '—'}${s.survey.date ? ` · ${s.survey.date}` : ''}<div class="tiny muted">${s.survey.team || ''}</div></dd>
      <dt>Elevation</dt><dd>${fmtM(s.elevation)}</dd>
      <dt>Ground above</dt><dd>${fmtM(s.ground)}</dd>
      <dt>Depth</dt><dd style=${s.depth < 0 ? 'color:#e87ba4' : ''}>${fmtM(s.depth)}${s.depth < 0 ? ' (plots above ground)' : ''}</dd>
      <dt>Position</dt><dd class="num tiny">E ${s.easting.toFixed(1)} · N ${s.northing.toFixed(1)}</dd>
      <${PropertyRow} e=${s.easting} n=${s.northing} />
      <${HydroRow} e=${s.easting} n=${s.northing} />
      <${WaterRow} station=${s.name} />
      ${s.formation && html`<dt>Formation</dt><dd><span class="sw" style=${'display:inline-block;width:10px;height:10px;border-radius:2px;margin-right:6px;background:' + s.formation.unit.color}></span>${s.formation.unit.name}
        <div class="tiny muted">3D model: ${s.formation.where}${s.formation.near.length ? ' · near the ' + s.formation.near.join(', ') : ''}</div></dd>`}</dl>
    ${s.survey.comment && html`<div class="small muted">${s.survey.comment}</div>`}
    ${cavePlaces.value.some(p => p.stations?.includes(s.name)) && html`<div class="sect">Places here</div>${cavePlaces.value.filter(p => p.stations?.includes(s.name)).map(p => html`
      <button class="tree-row" onClick=${() => selection.value = { type: 'place', id: p.id }}><span class="small">${p.name}</span></button>`)}`}
    ${nearby.length > 0 && html`<div class="sect">Recorded at this station</div><div class="list">${nearby.map(p => html`
      <button class="row-btn" onClick=${() => selection.value = { type: p.type, id: p.id, place: p }}><span class="dot" style=${'background:' + placeStyle(p).color}></span>
        <span class="t">${p.name}</span><span class="s">${p.date || p.kind}</span></button>`)}</div>`}`;
  return { body, title: s.name, sub: 'Survey station', actions };
}

function depressionView(d) {
  if (d.basin) {
    const actions = [canWrite() && html`<button class="btn sm primary" onClick=${() => openModal('feature', { preset: { kind: 'swallet', name: '',
      description: `Lowest point of a closed basin of ${(d.area / 10000).toFixed(1)} ha (LIDAR analysis).`, location: { mode: 'surface', easting: d.easting, northing: d.northing, elevation: d.ground } } })}>Record as swallet</button>`];
    const body = html`<dl class="kv"><dt>Basin area</dt><dd>${(d.area / 10000).toFixed(1)} ha</dd><dt>Lowest point</dt><dd>${fmtM(d.ground)}</dd>
      ${d.station && html`<dt>Nearest passage</dt><dd>${d.dist} m to station <b>${d.station}</b></dd>`}</dl>
      <div class="small muted">A closed basin too big to be a single sinkhole: water collecting here has no surface outlet, so it most likely sinks underground near this lowest point. Smaller sinks inside it are still highlighted.</div>`;
    return { body, title: 'Closed basin', sub: 'LIDAR analysis', actions };
  }
  const actions = [canWrite() && html`<button class="btn sm primary" onClick=${() => openModal('feature', { preset: { kind: 'sinkhole', name: '',
    description: `Closed depression from LIDAR: ${d.depth} m deep, ${d.area} m².`, location: { mode: 'surface', easting: d.easting, northing: d.northing, elevation: d.ground } } })}>Record as sinkhole</button>`];
  const body = html`<dl class="kv">
    <dt>Depth</dt><dd>${fmtM(d.depth)}</dd><dt>Area</dt><dd>${d.area.toLocaleString()} m²</dd>
    <dt>Floor</dt><dd>${fmtM(d.ground)}</dd>
    ${d.station && html`<dt>Nearest passage</dt><dd>${d.dist} m to station <b>${d.station}</b>, ${d.station_depth} m below the surface</dd>`}</dl>
    <div class="small muted">Found automatically by filling the LIDAR surface. Road fills over culverts and ponds can also show up; check before recording.</div>`;
  return { body, title: 'Closed depression', sub: 'LIDAR analysis', actions };
}
